import React, {useContext, useEffect, useRef, useState} from 'react';
import {
    CheckboxInput,
    DataGrid,
    DataGridDataRowProps,
    DataGridHeaderRowProps,
    Modal,
    RadioButtonGroup,
    SORT_ORDER,
    SortElementProps,
    Text
} from "atmosfair-ui";
import {CompanyAgencyAddress, ScrollDirection} from "../../../api/model/common";
import {
    apiSlice,
    useChangeUsersAgencyMutation,
    useDeleteUserMutation,
    useLazyGetAllAgenciesForGivenUserQuery,
    useMakeUserToAgencyAdminMutation,
    useResendInvitationEmailMutation,
    useUnmakeUserFromAgencyAdminMutation,
    useUpdateUserInfoAdminMutation
} from '../../../redux/reducer/api'
import {cloneDeep, isEqual} from "lodash";
import {
    PersonalUserInformation,
    SortUsers,
    UserProps,
    WindowedUserAgenciesAdminOf,
    WindowedUserProps
} from "../../../api/model/user";
import {useTranslation} from 'react-i18next';
import {v4 as uuidv4} from "uuid";
import {NO_DATA_STRING} from "../../../utils/string/common";
import SearchInput from "../../../molecules/search-input/SearchInput";
import SectionTitle from "../section-title/SectionTitle";
import {theme} from "../../../styles/theme";
import ErrorText from "../../../molecules/errors/error-text/ErrorText";
import {VALIDATE_FORM_STARTING_NUMBER} from "../../../constants/default-values";
import UsersForm, {UserFormProps} from "../../forms/users-form/UsersForm";
import {AgencyProps} from "../../../api/model/agency";
import AgenciesCompanyDataGrid from "../agencies-company/agencies-company-data-grid/AgenciesCompanyDataGrid";
import {AgenciesCompanyFormProps} from "../../forms/agencies-company-form/AgenciesCompanyForm";
import useFirstRender from '../../../hooks/use-first-render';
import {toast} from "react-toastify";
import ToastWithTextOnly from "../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {InstanceContext} from "../../protected-layout/ProtectedLayout";
import {getFullName} from "../../../utils/getter/common";
import {useAppDispatch} from "../../../redux/reducer/store";

const DEFAULT_SORT_VARIABLE = SortUsers.lastName
const DEFAULT_SORT_ORDER = SORT_ORDER.ASC

interface OriginalUserObjectProps extends Omit<PersonalUserInformation, 'internationalPhonePrefix'> {
    internationalPhonePrefix: CompanyAgencyAddress["internationalPhonePrefix"] | null,
}

interface ModalProps {
    deleteUser: {
        modalVisible: boolean,
        user: WindowedUserProps | null,
    },
    editUser: {
        modalVisible: boolean,
        user: WindowedUserProps | null,
        formData: UserFormProps,
        originalUserObject: OriginalUserObjectProps,
    },
    changeUsersAgency: {
        modalVisible: boolean,
        user: WindowedUserProps | null,
    },
    makeUnmakeUseAdmin: {
        modalVisible: boolean,
        user: WindowedUserProps | null,
        newAgencyIndex: number,
        originalAgencyAdmin: boolean,
        agencyAdmin: boolean,
        originalAdminOfAgency: WindowedUserAgenciesAdminOf | null,
        newAdminOfAgency: WindowedUserAgenciesAdminOf | null
    }
}

const DEFAULT_FORM_DATA = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    internationalPhonePrefix: "",
}

const DEFAULT_MODAL_DATA: ModalProps = {
    deleteUser: {
        modalVisible: false,
        user: null,
    },
    editUser: {
        modalVisible: false,
        user: null,
        formData: DEFAULT_FORM_DATA,
        originalUserObject: {} as OriginalUserObjectProps,
    },
    changeUsersAgency: {
        modalVisible: false,
        user: null,
    },
    makeUnmakeUseAdmin: {
        modalVisible: false,
        user: null,
        newAgencyIndex: 0,
        agencyAdmin: false,
        originalAgencyAdmin: false,
        originalAdminOfAgency: null,
        newAdminOfAgency: null
    }
}


interface AllUsersDataGridProps {
    allUsers: AgenciesCompanyFormProps['allUsers'],
    userData: UserProps
}

const AllUsersDataGrid = ({
                              allUsers,
                              userData
                          }: AllUsersDataGridProps) => {
    const [modalProps, setModalProps] = useState<ModalProps>(DEFAULT_MODAL_DATA)
    const [resetCache, setResetCache] = useState(0)
    const [validateForm, setValidateForm] = useState(VALIDATE_FORM_STARTING_NUMBER)
    const [fetchRowsStatus, setFetchRowStatus] = useState({
        isErrorAllUsersForKlimaLinkAdmin: false
    })
    const {isErrorAllUsersForKlimaLinkAdmin} = fetchRowsStatus
    const [headers, setHeaders] = useState<DataGridHeaderRowProps["headers"]>([])
    const [sortVariable, setSortVariable] = useState<SortUsers>(DEFAULT_SORT_VARIABLE)
    const [sortingOrder, setSortingOrder] = useState<SortElementProps["active"]>(DEFAULT_SORT_ORDER)
    const [usersCount, setUsersCount] = useState<number | null>(null)
    const [rows, setRows] = useState<DataGridDataRowProps["rows"]>([])
    const nextScrollPosition = useRef<string | null>(null)
    const isDataFinished = useRef<boolean>(false)
    const searchTerm = useRef<string | null>(null)
    const {t} = useTranslation()
    const {disableButtonOnNotProduction} = useContext(InstanceContext);
    const userResendInvitationEmail = useRef<string | null>(null)
    const isInitialRender = useFirstRender()
    const dispatch = useAppDispatch()
    const [getAllAgenciesForGivenUser, {
        isError: isErrorGetAllAgenciesForGivenUser,
        isLoading: isLoadingGetAllAgenciesForGivenUser,
        data: getAllAgenciesForGivenUserResponse,
    }] = useLazyGetAllAgenciesForGivenUserQuery()
    const [deleteUser, {
        isLoading: isLoadingDeleteUser,
        data: deleteUserResponse,
    }] = useDeleteUserMutation()
    const [updateUserInfo, {
        isLoading: isLoadingUpdateUserInfo,
        data: updateUserInfoResponse,
    }] = useUpdateUserInfoAdminMutation()
    const [makeUserToAgencyAdmin, {
        isLoading: isLoadingMakeUserAdminToAgencyAdmin,
        data: makeUserToAgencyAdminResponse,
    }] = useMakeUserToAgencyAdminMutation()
    const [unmakeUserFromAgencyAdmin, {
        isLoading: isLoadingUnmakeUserFromAgencyAdmin,
        data: unmakeUserFromAgencyAdminResponse,
    }] = useUnmakeUserFromAgencyAdminMutation()
    const [resendInvitationEmail, {
        isLoading: isLoadingResendInvitationEmail,
        isSuccess: isSuccessResendInvitationEmail,
        isError: isErrorResendInvitationEmail,
    }] = useResendInvitationEmailMutation()
    const [changeUsersAgency, {
        isLoading: isLoadingChangeUsersAgency,
    }] = useChangeUsersAgencyMutation()

    useEffect(() => {
        if (!isInitialRender) {
            _resetModal()
            const fetchData = async () => {
                _resetOnFetch()
                const newRows = await _fetchRows()
                setRows(newRows)
            }

            fetchData().catch((error) => {
                console.error("Error fetching data:", error);
            });
        }
    }, [
        sortingOrder,
        sortVariable,
        makeUserToAgencyAdminResponse,
        deleteUserResponse,
        unmakeUserFromAgencyAdminResponse,
        updateUserInfoResponse
    ])

    useEffect(() => {
        setHeaders(_setHeaders())
    }, [])

    useEffect(() => {
        if (isSuccessResendInvitationEmail && userResendInvitationEmail.current !== null) {
            toast(<ToastWithTextOnly
                    message={t("TOASTS.SUCCESS.RESEND_INVITATION_EMAIL",
                        {email: userResendInvitationEmail.current}
                    )}/>,
                {progress: undefined, type: "success"}
            );
            userResendInvitationEmail.current = null
        }
    }, [isSuccessResendInvitationEmail]);

    useEffect(() => {
        if (isErrorResendInvitationEmail && userResendInvitationEmail.current !== null) {
            toast(<ToastWithTextOnly
                    message={t("TOASTS.ERROR.RESEND_INVITATION_EMAIL",
                        {email: userResendInvitationEmail.current}
                    )}/>,
                {progress: undefined, type: "error"}
            );
            userResendInvitationEmail.current = null
        }
    }, [isErrorResendInvitationEmail]);

    const _resetOnFetch = () => {
        nextScrollPosition.current = null
        isDataFinished.current = false
        setRows([])
    }

    const _setHeaders = () => {
        return [
            {
                text: "GENERAL.FIRST_NAME",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortUsers.firstName || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortUsers.firstName);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetch()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortUsers.firstName || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortUsers.firstName);
                            setSortingOrder(SORT_ORDER.DESC);
                            _resetOnFetch()
                        }
                    },
                    disabled: false,
                },
            },
            {
                text: "GENERAL.LAST_NAME",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortUsers.lastName || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortUsers.lastName);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetch()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortUsers.lastName || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortUsers.lastName);
                            setSortingOrder(SORT_ORDER.DESC);
                            _resetOnFetch()
                        }
                    },
                    disabled: false,
                },
            },
            {text: "GENERAL.EMAIL"},
            {
                text: "GENERAL.COMPANY",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortUsers.companyName || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortUsers.companyName);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetch()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortUsers.companyName || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortUsers.companyName);
                            setSortingOrder(SORT_ORDER.DESC)
                            _resetOnFetch()
                        }
                    },
                    disabled: false,
                },
            },
            {text: "GENERAL.COMPANY_ADMIN"},
        ]

    }

    const _fetchRows = async (): Promise<DataGridDataRowProps["rows"]> => {
        setFetchRowStatus({
            isErrorAllUsersForKlimaLinkAdmin: false
        })
        let newRows: DataGridDataRowProps["rows"] = []
        let error = false
        if (!isDataFinished.current) {
            try {
                // needs this style of using the endpoint to bypass cache
                // when a user i.e. is edited and updated, the request is made with the same parameters
                // redux rtk caches the the result. InvlidateCaches does not work for lazy functions.
                // this is a workaround to force a refetch
                const action = dispatch(
                    apiSlice.endpoints.getAllUsersForKlimaLinkAdmin.initiate(
                        {
                            scrollPosition: nextScrollPosition.current,
                            size: 20,
                            direction: ScrollDirection.forward,
                            sort: sortVariable,
                            sortOrder: sortingOrder,
                            searchTerm: searchTerm.current,
                        },
                        {forceRefetch: true}
                    )
                )
                const responseData = await action.unwrap()
                nextScrollPosition.current = (responseData?.nextPosition ?? null);
                if (responseData?.nextPosition === null || responseData?.nextPosition === "null") {
                    isDataFinished.current = true;
                }
                newRows = _toUsersDataGrid(responseData.users)
                setUsersCount(responseData.usersCount)

                if (isDataFinished.current && searchTerm.current === null) {
                    const updatedRows = cloneDeep(rows) as DataGridDataRowProps["rows"]
                    newRows.forEach(newRow => updatedRows.push(newRow))
                    newRows = updatedRows
                }
                action.unsubscribe()
            } catch (error) {
                console.warn("Something went wrong getting users: ", error)
            }
        }
        setFetchRowStatus({
            isErrorAllUsersForKlimaLinkAdmin: error,
        })
        return newRows
    }

    const _setDataGridTooltipsAndDisabled = (
        registrationComplete: boolean,
        email: string,
        isCompanyAdmin: boolean,
        isKlimaLinkAdmin: boolean,
        isAgencyAdmin: boolean,
        deactivated: boolean
    ) => {
        let disableEdit, disableDelete, disableChangeUsersAgency, disableResendInvitationEmail,
            disableMakeUnmakeUserAdmin = false
        let editTooltip, deleteTooltip, changeUsersAgencyTooltip, resendInvitationEmailTooltip,
            makeUnmakeUserAdminTooltip = ''
        if (disableButtonOnNotProduction || deactivated) {
            disableEdit = true
            disableDelete = true
            disableChangeUsersAgency = true
            disableMakeUnmakeUserAdmin = true
            disableResendInvitationEmail = true
            if (disableButtonOnNotProduction) {
                const notOnProductionTooltip = "TOOLTIP.COMMON.DISABLED_FUNCTIONALITY_DEV_INSTANCE"
                editTooltip = notOnProductionTooltip
                deleteTooltip = notOnProductionTooltip
                changeUsersAgencyTooltip = notOnProductionTooltip
                resendInvitationEmailTooltip = notOnProductionTooltip
                makeUnmakeUserAdminTooltip = notOnProductionTooltip
            } else {
                const userDeactivated = "TOOLTIP.SETTINGS.USER_DEACTIVATED"
                editTooltip = userDeactivated
                deleteTooltip = userDeactivated
                changeUsersAgencyTooltip = userDeactivated
                resendInvitationEmailTooltip = userDeactivated
                makeUnmakeUserAdminTooltip = userDeactivated
            }
        } else {
            editTooltip = 'TOOLTIP.SETTINGS.EDIT_USER'
            deleteTooltip = 'TOOLTIP.SETTINGS.DELETE_USER'
            if (isKlimaLinkAdmin) {
                disableDelete = true
                disableChangeUsersAgency = true
                disableMakeUnmakeUserAdmin = true

                deleteTooltip = "TOOLTIP.SETTINGS.DELETE_KLIMALINK_ADMIN"
                changeUsersAgencyTooltip = "TOOLTIP.SETTINGS.CHANGE_AGENCY_KLIMALINK_ADMIN"
                makeUnmakeUserAdminTooltip = "TOOLTIP.SETTINGS.MAKE_UNMAKE_AGENCY_ADMIN_KLIMALINK"
            } else if (isCompanyAdmin) {
                disableChangeUsersAgency = true
                disableMakeUnmakeUserAdmin = true

                changeUsersAgencyTooltip = "TOOLTIP.SETTINGS.CHANGE_USERS_AGENCY_NOT_POSSIBLE_COMPANY_ADMIN"
                makeUnmakeUserAdminTooltip = "TOOLTIP.SETTINGS.MAKE_UNMAKE_AGENCY_ADMIN_COMPANY_ADMIN"
                deleteTooltip = "TOOLTIP.SETTINGS.DELETE_COMPANY_ADMIN"
            } else {
                if (isAgencyAdmin) {
                    disableChangeUsersAgency = true

                    changeUsersAgencyTooltip = 'TOOLTIP.SETTINGS.CHANGE_AGENCY_OTHER_ADMIN'
                    makeUnmakeUserAdminTooltip = 'TOOLTIP.SETTINGS.REMOVE_ADMIN'
                } else {
                    makeUnmakeUserAdminTooltip = 'TOOLTIP.SETTINGS.MAKE_ADMIN'
                    changeUsersAgencyTooltip = "TOOLTIP.SETTINGS.CHANGE_USERS_AGENCY"
                }
            }
            if (registrationComplete) {
                disableResendInvitationEmail = true

                resendInvitationEmailTooltip = "TOOLTIP.SETTINGS.RESEND_INVITATION_EMAIL_ALREADY_REGISTERED"
            } else {
                resendInvitationEmailTooltip = t("TOOLTIP.SETTINGS.RESEND_INVITATION_EMAIL", {
                    email,
                    interpolation: {escapeValue: false}
                })
            }
        }
        return {
            editTooltip, deleteTooltip, changeUsersAgencyTooltip, resendInvitationEmailTooltip,
            makeUnmakeUserAdminTooltip, disableEdit, disableDelete, disableChangeUsersAgency,
            disableResendInvitationEmail, disableMakeUnmakeUserAdmin
        }
    }

    const _setModalProps = <K extends keyof ModalProps>(key: K, payload: ModalProps[K]) => setModalProps({
        ...modalProps,
        [key]: {
            ...modalProps[key],
            ...payload
        }
    })

    const _toUsersDataGrid = (users: WindowedUserProps[]) => users.map(user => {
        const {
            firstName,
            lastName,
            email,
            companyName,
            isKlimaLinkAdmin,
            isCompanyAdmin,
            registrationComplete,
            id,
            deactivated,
            agenciesAdminOf
        } = user
        const isSameUser = userData.id === id
        const isAdminOfAnyAgency = agenciesAdminOf.length > 0
        const {
            editTooltip,
            deleteTooltip,
            changeUsersAgencyTooltip,
            resendInvitationEmailTooltip,
            makeUnmakeUserAdminTooltip,
            disableEdit,
            disableDelete,
            disableChangeUsersAgency,
            disableResendInvitationEmail,
            disableMakeUnmakeUserAdmin
        } = _setDataGridTooltipsAndDisabled(registrationComplete, email, isCompanyAdmin, isKlimaLinkAdmin, isAdminOfAnyAgency, deactivated)
        const companyAdminText = isCompanyAdmin ? "GENERAL.YES" : "GENERAL.NO"
        const companyNameText = companyName ?? NO_DATA_STRING

        return {
            data: [
                {
                    text: firstName,
                    value: firstName,
                },
                {
                    text: `${lastName}${!registrationComplete ? "*" : ""}`,
                    value: lastName,
                },
                {
                    text: email,
                    value: email,
                },
                {
                    text: companyNameText,
                    value: companyNameText,
                },
                {
                    text: companyAdminText,
                    value: companyAdminText,
                },
            ],
            actions: [
                {
                    hide: isSameUser,
                    disabled: disableEdit,
                    icon: "penToSquare",
                    tooltip: editTooltip,
                    onClick: (_, user) => {
                        const {
                            firstName,
                            lastName,
                            email,
                            phoneNumber,
                            internationalPhonePrefix,
                        } = user
                        _setModalProps("editUser", {
                            ...modalProps.editUser,
                            originalUserObject: {
                                firstName,
                                lastName,
                                email,
                                phoneNumber,
                                internationalPhonePrefix,
                            },
                            formData: {
                                firstName,
                                lastName,
                                phoneNumber,
                                internationalPhonePrefix,
                                email,
                            },
                            user,
                            modalVisible: true,
                        })
                    }
                },
                {
                    hide: isSameUser,
                    disabled: disableDelete,
                    icon: "trash",
                    tooltip: deleteTooltip,
                    onClick: (_, user) => {
                        _setModalProps("deleteUser", {
                            modalVisible: true,
                            user: user,
                        })
                        getAllAgenciesForGivenUser(user.email)
                    }
                },
                {
                    hide: isSameUser,
                    disabled: disableMakeUnmakeUserAdmin,
                    icon: isAdminOfAnyAgency ? "userTie" : "crown",
                    tooltip: makeUnmakeUserAdminTooltip,
                    onClick: (_, user) => {
                        const agencyAdmin = user.agenciesAdminOf.length > 0
                        _setModalProps("makeUnmakeUseAdmin", {
                            ...modalProps.makeUnmakeUseAdmin,
                            user,
                            modalVisible: true,
                            originalAgencyAdmin: agencyAdmin,
                            agencyAdmin,
                            newAdminOfAgency: null,
                            originalAdminOfAgency: agencyAdmin ? user.agenciesAdminOf[0] : null,

                        })
                        getAllAgenciesForGivenUser(user.email)
                    }
                },
                {
                    hide: isSameUser,
                    disabled: disableChangeUsersAgency,
                    icon: "repeat",
                    tooltip: changeUsersAgencyTooltip,
                    onClick: (_, user) => {
                        _setModalProps("changeUsersAgency", {
                            user,
                            modalVisible: true
                        })
                        getAllAgenciesForGivenUser(user.email)
                    }
                },
                {
                    hide: isSameUser,
                    disabled: disableResendInvitationEmail,
                    icon: "envelope",
                    tooltip: resendInvitationEmailTooltip,
                    onClick: (_, user) => {
                        console.log(_, user)
                        const {email, agencyIds} = user
                        userResendInvitationEmail.current = email
                        if (agencyIds.length > 1) {
                            toast(<ToastWithTextOnly
                                    message={t("TOASTS.ERROR.TOO_MANY_AGENCIES_INVITATION",
                                        {email: userResendInvitationEmail.current}
                                    )}/>,
                                {progress: undefined, type: "error"}
                            );
                        } else {
                            resendInvitationEmail({email, agencyId: agencyIds[0] || null})
                        }
                    }
                },
            ],
            key: uuidv4(),
            rawData: user,
            tooltip: deactivated ? "TOOLTIP.SETTINGS.USER_DEACTIVATED" : undefined,
            disabled: deactivated
        } as DataGridDataRowProps["rows"][0]
    })

    const _onChange = async (query: string) => {
        nextScrollPosition.current = null
        isDataFinished.current = false
        const queryForSearch = query.length > 0 ? query : null
        if (queryForSearch === null) setResetCache(resetCache + 1)
        searchTerm.current = queryForSearch
        const newRows = await _fetchRows()
        setRows(newRows)
    }

    const _deleteUser = (user: WindowedUserProps | null) => {
        if (user != null) {
            deleteUser({userEmail: user.email})
            _resetModal()
        }
    }

    const _renderDataGrid = () => {
        if (isErrorAllUsersForKlimaLinkAdmin) return <ErrorText>ERRORS.GENERAL_NETWORK_ERROR_BODY</ErrorText>
        return <DataGrid
            resetCache={resetCache}
            look={"primaryLight"}
            headers={headers}
            rows={rows}
            rowSize={"medium"}
            fetchRows={isInitialRender ? undefined : _fetchRows}
        />
    }

    const _renderDeleteUserModalContent = (user: WindowedUserProps | null) => {
        if (isErrorGetAllAgenciesForGivenUser) return <ErrorText>ERRORS.GENERAL_NETWORK_ERROR_BODY</ErrorText>
        const userName = getFullName(user)
        if (getAllAgenciesForGivenUserResponse && getAllAgenciesForGivenUserResponse.assignedAgencies.length > 0) {
            const agencies = getAllAgenciesForGivenUserResponse.assignedAgencies.map(agency => agency.name).join(", ")
            return <>
                <Text>{t("SCREENS.SETTINGS.DELETE_USER_KLIMALINK_TEXT", {
                    user: userName,
                    agencies,
                    interpolation: {escapeValue: false}
                })}</Text>
                {user?.isCompanyAdmin &&
                    <Text>SCREENS.SETTINGS.DELETE_COMPANY_ADMIN_KLIMALINK_TEXT</Text>}
            </>
        } else {
            return <>
                <Text>{t("SCREENS.SETTINGS.DELETE_USER_TEXT", {
                    user: userName,
                    interpolation: {escapeValue: false}
                })}</Text>
                {user?.isCompanyAdmin &&
                    <Text>SCREENS.SETTINGS.DELETE_COMPANY_ADMIN_KLIMALINK_TEXT</Text>}
            </>
        }
    }

    const _editUser = (
        updateUserData: UserFormProps,
        originalUserData: OriginalUserObjectProps,
    ) => {
        const {
            firstName,
            lastName,
            email,
            phoneNumber,
            internationalPhonePrefix,
        } = updateUserData
        const updatedUserObject = {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phoneNumber: phoneNumber.trim(),
            internationalPhonePrefix: internationalPhonePrefix!!,
        }
        if (!isEqual(updatedUserObject, originalUserData) && email) updateUserInfo({
            userObject: updatedUserObject,
            userEmail: email
        })
        _resetModal()
    }

    const _onConfirmChangeUsersAgency = (user: WindowedUserProps | null, newAgency: AgencyProps) => {
        if (user && getAllAgenciesForGivenUserResponse && getAllAgenciesForGivenUserResponse.assignedAgencies.length > 0) {
            changeUsersAgency({
                oldAgencyId: getAllAgenciesForGivenUserResponse.assignedAgencies[0].id, // fe currently handles only user being in one agency
                newAgencyId: newAgency.id,
                userEmail: user.email
            })
        }
        _resetModal()
    }

    const _resetModal = () => {
        setValidateForm(VALIDATE_FORM_STARTING_NUMBER)
        setModalProps(DEFAULT_MODAL_DATA)
    }

    const _renderDeleteUserModal = () => {
        const {modalVisible, user} = modalProps.deleteUser

        return <Modal
            headline={isLoadingGetAllAgenciesForGivenUser ? "GENERAL.LOADING" : t("SCREENS.SETTINGS.DELETE_USER", {
                user: getFullName(user),
                interpolation: {escapeValue: false}
            })}
            onEnterKeyPressed={() => _deleteUser(user)}
            show={modalVisible}
            closeButtonText={"GENERAL.ABORT"}
            confirmButtonText={"GENERAL.DELETE"}
            confirmButtonColor={"warningMain"}
            onConfirm={() => _deleteUser(user)}
            onBackdropClick={_resetModal}
            onClose={_resetModal}
            showSpinner={isLoadingGetAllAgenciesForGivenUser}
            showPaper={true}>
            {_renderDeleteUserModalContent(user)}
        </Modal>
    }

    const _renderEditUserModalContent = () => {
        if (isErrorGetAllAgenciesForGivenUser) return <ErrorText>ERRORS.GENERAL_NETWORK_ERROR_BODY</ErrorText>
        const {formData, modalVisible, originalUserObject} = modalProps.editUser
        return <UsersForm
            onEnterKeyPressed={() => setValidateForm(validateForm + 1)}
            defaultValues={formData}
            showAgencySelection={false}
            showEmailField={false}
            showPhoneNumberFields={modalVisible}
            showIsAdminCheckbox={false}
            validateForm={validateForm}
            onFormIsValid={newUserData => _editUser(newUserData, originalUserObject)}
            agencies={[]}/>
    }

    const _renderEditUserModal = () => {
        const {modalVisible, user} = modalProps.editUser

        return <Modal
            headline={t("SCREENS.SETTINGS.EDIT_USER", {
                name: getFullName(user),
                interpolation: {escapeValue: false}
            })}
            show={modalVisible}
            closeButtonText={"GENERAL.ABORT"}
            confirmButtonText={"GENERAL.OK"}
            onConfirm={() => setValidateForm(validateForm + 1)}
            onBackdropClick={_resetModal}
            onClose={_resetModal}
            showPaper={true}>
            {_renderEditUserModalContent()}
        </Modal>
    }

    const _renderChangeUsersAgencyModal = () => {
        const {modalVisible, user} = modalProps.changeUsersAgency
        return <Modal
            headline={user != null ? t(`MODALS.SETTINGS.CHANGE_USERS_AGENCY_TITLE`, {
                name: getFullName(user),
                interpolation: {escapeValue: false}
            }) : t("GENERAL.LOADING")}
            showSpinner={isLoadingGetAllAgenciesForGivenUser}
            show={modalVisible}
            closeButtonText={"GENERAL.ABORT"}
            onBackdropClick={_resetModal}
            onClose={_resetModal}
            showPaper={true}>
            <>
                <Text style={{marginBottom: theme.spacer.large}}>
                    {getAllAgenciesForGivenUserResponse && getAllAgenciesForGivenUserResponse.assignedAgencies.length > 0 ?
                        `${t("MODALS.SETTINGS.CHANGE_USERS_AGENCY_TEXT", {
                            name: getFullName(user),
                            oldAgency: getAllAgenciesForGivenUserResponse.assignedAgencies[0].name, // FE currently handles only one agency
                            interpolation: {escapeValue: false}
                        })}` :
                        t("GENERAL.LOADING")
                    }
                </Text>
                <AgenciesCompanyDataGrid
                    agenciesToHide={getAllAgenciesForGivenUserResponse ? getAllAgenciesForGivenUserResponse.assignedAgencies.map(agency => agency.id) : []}
                    allUsers={allUsers}
                    isCompanyAdmin={true}
                    whichType={"agencies"}
                    displayInSelectAgencyModal={true}
                    onSelectAgency={agency => _onConfirmChangeUsersAgency(user, agency)}
                    isKlimaLinkAdmin={true}/>
                <div style={{height: '20px'}}/>
            </>
        </Modal>
    }

    const _onConfirmMakeUnmakeUserAdmin = () => {
        const {
            user,
            agencyAdmin,
            originalAdminOfAgency,
            originalAgencyAdmin,
            newAdminOfAgency
        } = modalProps.makeUnmakeUseAdmin
        if (user) {
            if (originalAgencyAdmin === agencyAdmin && originalAdminOfAgency?.id === newAdminOfAgency?.id) return
            if (newAdminOfAgency) {
                const {email: userEmail} = user
                const {id: agencyId} = newAdminOfAgency
                if (agencyAdmin) {
                    makeUserToAgencyAdmin({
                        userEmail,
                        agencyId
                    })
                } else {
                    unmakeUserFromAgencyAdmin({
                        userEmail,
                        agencyId
                    })
                }
            }
        }
    }

    const _renderMakeUnmakeUserAdminModalContent = (props: ModalProps["makeUnmakeUseAdmin"]) => {
        const {user, agencyAdmin, originalAgencyAdmin} = props
        if (getAllAgenciesForGivenUserResponse && getAllAgenciesForGivenUserResponse.assignedAgencies.length > 0 && user) {
            const {newAgencyIndex} = modalProps.makeUnmakeUseAdmin
            const {agenciesAdminOf} = user
            const possibleAgencies = getAllAgenciesForGivenUserResponse.possibleAgencies
                .filter(agency => !user.agenciesAdminOf?.map(agencyAdmin => agencyAdmin.id).includes(agency.id))
            const radioButtonAgencies = possibleAgencies.map((filteredAgency, index) => ({
                label: filteredAgency.name,
                value: index,
                id: filteredAgency.id,
            }))
            return <>
                <Text>{t(
                    `MODALS.SETTINGS.MAKE_UNMAKE_ADMIN_TEXT`,
                    {user: getFullName(user), interpolation: {escapeValue: false}}
                )}
                </Text>
                {originalAgencyAdmin &&
                    <Text>{t(
                        `MODALS.SETTINGS.MAKE_UNMAKE_ADMIN_IS_ADMIN_TEXT`,
                        {
                            user: getFullName(user),
                            agency: possibleAgencies[0].name, // user can only be in one agency in FE
                            interpolation: {escapeValue: false}
                        }
                    )}</Text>
                }
                {!originalAgencyAdmin && <RadioButtonGroup
                    fieldsetLabel={"LABELS.SETTINGS.CHOOSE_USERS_AGENCY_ADMIN"}
                    containerStyle={{marginTop: theme.spacer.large}}
                    onChange={index => {
                        const agency = possibleAgencies[index]
                        _setModalProps("makeUnmakeUseAdmin", {
                            ...modalProps.makeUnmakeUseAdmin,
                            newAgencyIndex: index,
                            agencyAdmin: true,
                            newAdminOfAgency: {
                                id: agency.id,
                                name: agency.name
                            }
                        })
                    }
                    }
                    radioButtons={radioButtonAgencies}
                    selected={newAgencyIndex}
                    name={"SCREENS.TRAVEL_COMPONENTS_OVERVIEW.RADIO_BUTTON_GROUP"}
                    orientation={"vertical"}
                    labelStyle={{fontSize: theme.font.fontSize.regular}}
                    hideLabel={false}/>}
                {originalAgencyAdmin && <CheckboxInput
                    hideLabel={true}
                    containerStyle={{marginTop: theme.spacer.large}}
                    text={`${agencyAdmin ? t("TOOLTIP.SETTINGS.REMOVE_ADMIN_KLIMALINK", {
                        agency: agenciesAdminOf[0].name
                    }) : t("TOOLTIP.SETTINGS.MAKE_ADMIN_KLIMALINK", {
                        agency: agenciesAdminOf[0].name
                    })}`
                    }
                    checked={agencyAdmin}
                    label={"SCREENS.SETTINGS.IS_ADMIN_LABEL"}
                    onChange={() => _setModalProps("makeUnmakeUseAdmin", {
                        ...modalProps.makeUnmakeUseAdmin,
                        agencyAdmin: !agencyAdmin,
                        newAdminOfAgency: modalProps.makeUnmakeUseAdmin.originalAdminOfAgency,
                    })}/>}

            </>
        }
    }

    const _renderMakeUnmakeUserAdminModal = () => {
        const {makeUnmakeUseAdmin} = modalProps
        const {modalVisible, user} = makeUnmakeUseAdmin
        return <Modal
            headline={t(
                `MODALS.SETTINGS.MAKE_UNMAKE_ADMIN_TEXT`,
                {user: getFullName(user), interpolation: {escapeValue: false}}
            )}
            showSpinner={isLoadingGetAllAgenciesForGivenUser}
            show={modalVisible}
            closeButtonText={"GENERAL.ABORT"}
            confirmButtonText={"GENERAL.OK"}
            onConfirm={() => _onConfirmMakeUnmakeUserAdmin()}
            onEnterKeyPressed={_onConfirmMakeUnmakeUserAdmin}
            onBackdropClick={_resetModal}
            onClose={_resetModal}
            showPaper={true}>
            {_renderMakeUnmakeUserAdminModalContent(makeUnmakeUseAdmin)}

        </Modal>
    }

    return <section>
        <SectionTitle
            isLoading={
                isLoadingDeleteUser ||
                isLoadingUpdateUserInfo ||
                isLoadingResendInvitationEmail ||
                isLoadingMakeUserAdminToAgencyAdmin ||
                isLoadingUnmakeUserFromAgencyAdmin ||
                isLoadingChangeUsersAgency ||
                isLoadingGetAllAgenciesForGivenUser
            }
            subTitle={usersCount !== null ? `${t("GENERAL.TOTAL")}: ${usersCount} ${t("GENERAL.EXPEDIENTS")}` : undefined}
            title={"GENERAL.EXPEDIENTS"}/>
        <div style={{width: '100%'}}>
            <SearchInput
                label={"SCREENS.SETTINGS.SEARCH_BAR_LABEL_MEMBERS"}
                placeholder={"GENERAL.SEARCH"}
                onChange={_onChange}
            />
        </div>
        <div className={"agencies-company-wrapper"}>
            {_renderDataGrid()}
            <Text
                style={{marginTop: theme.spacer.large}}>
                {`*${t("SCREENS.SETTINGS.USER_REGISTRATION_NOT_COMPLETED")}`}
            </Text>
        </div>
        {_renderDeleteUserModal()}
        {_renderEditUserModal()}
        {_renderChangeUsersAgencyModal()}
        {_renderMakeUnmakeUserAdminModal()}
    </section>
}

export default AllUsersDataGrid